/**
 * FachpersonSigninForm — Therapeut-Login mit E-Mail + Passwort.
 * Extrahiert aus `pages/Login.tsx` (UX-Audit Phase 5).
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  fachpersonSigninSchema,
  type FachpersonSigninInput,
} from '@/schemas/auth';

interface Props {
  onSubmit: (values: FachpersonSigninInput) => Promise<void>;
  /**
   * Optional: called when the user clicks "Passwort vergessen?".
   * Receives the current email field value so the caller can pre-fill
   * the reset flow.
   */
  onForgotPassword?: (email: string) => void;
}

export function FachpersonSigninForm({ onSubmit, onForgotPassword }: Props) {
  const form = useForm<FachpersonSigninInput>({
    resolver: zodResolver(fachpersonSigninSchema),
    defaultValues: { email: '', password: '' },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem className="space-y-1.5">
              <FormLabel className="text-xs text-muted-foreground">E-Mail</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  required
                  placeholder="praxis@beispiel.de"
                  className="bg-secondary border-border focus:ring-1 focus:ring-primary"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem className="space-y-1.5">
              <div className="flex items-center justify-between">
                <FormLabel className="text-xs text-muted-foreground">Passwort</FormLabel>
                {onForgotPassword && (
                  <button
                    type="button"
                    onClick={() => onForgotPassword(form.getValues('email'))}
                    className="text-xs text-primary hover:underline focus-visible:outline-none focus-visible:underline"
                  >
                    Passwort vergessen?
                  </button>
                )}
              </div>
              <FormControl>
                <Input
                  type="password"
                  required
                  className="bg-secondary border-border focus:ring-1 focus:ring-primary"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="submit"
          disabled={form.formState.isSubmitting}
          className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl h-10"
        >
          {form.formState.isSubmitting && (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          )}
          Anmelden
        </Button>
      </form>
    </Form>
  );
}
